import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import styled from '@emotion/styled';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import { LearningPathStore, LearningPathWorksheetStore } from '@seedlang/stores';
import { Exercise, PodcastEpisode, WorksheetTag } from '@seedlang/models';
import Spinner from 'components/spinner';
import { gaps } from 'components/layout/stack';
import LearningPathDropdown from 'components/learning_path/learning_path_dropdown';
import { AppUI, ExerciseUI } from '@seedlang/state';
import WorksheetPreview from 'components/worksheet/worksheet_preview';
import WelcomeScreen from 'components/learning_path/welcome_screen';
import ScrollTarget from 'components/learning_path/scroll_target';
import DesktopPath from 'components/learning_path/desktop_path';
import MobilePath from 'components/learning_path/mobile_path';
import DesktopOnly from 'utils/desktop_only';
import eventParamsHelper from 'utils/event_params_helper';

const Wrapper = styled.div`
  margin: 0 auto -10px auto !important;
  display: flex;
  flex-direction: column;
  gap: ${gaps.md};
  width: 100%;
`;

const WorksheetPreviewWrapper = styled.div`
  flex: 2;
  padding-bottom: ${gaps.md};
`;

const ScrollTargetWrapper = styled.div`
  flex: 1;
`;

const WelcomeScreenWrapper = styled.div`
  padding: ${gaps.md} 0;

  @media (max-width: 960px) {
    padding: 0;
  }
`;

const PreviewAndPathGrid = styled.div`
  display: flex;
  align-items: start;
  gap: ${gaps.lg};
  margin: auto;

  @media (max-width: 699px) {
    grid-template-columns: 1fr;
  }
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

@observer
class LearningPath extends React.Component {
  @observable selectedWorksheetId = null;
  @observable showWelcomeScreen = false;
  trackedEvents = new Set();

  componentDidMount() {
    if (AppUI.userIsSignedInAndNotGuest) {
      const pathIdFromUserGroup = AppUI.user.currentUserGroup?.learningPathId;
      if (pathIdFromUserGroup) {
        AppUI.cookieStore.set('selectedLearningPathId', pathIdFromUserGroup);
      }
    }

    LearningPathStore.getIndex({
      filters: { published: true },
      queryStrings: { include_progress: !!this.selectedLearningPathId },
    }, response => {
      LearningPathStore.setIndexData(response);

      const pathId = this.selectedLearningPathId;
      if (!pathId || !response.some(item => item.id === pathId)) {
        AppUI.cookieStore.set('selectedLearningPathId', null);
        this.trackViewLearn();
        return;
      }

      this.loadSelectedWorksheetFromStorage(pathId);

      // adding ?welcome forces to show the welcome screen
      if (new URLSearchParams(window.location.search).has('welcome')) {
        this.showWelcomeScreen = true;
        this.trackViewLearn();
        return;
      }

      this.loadLearningPath(pathId);
    });
  }
  loadLearningPath = (learningPathId) => {
    LearningPathStore.getShow({
      ids: { learningPathId },
    }, response => {
      LearningPathStore.setShowData(response);
      this.trackViewLearn(response);
      if (this.selectedWorksheetId) {
        this.loadSelectedWorksheet();
      } else {
        this.loadFirstWorksheet(learningPathId);
      }
    });
  }
  loadFirstWorksheet = (learningPathId) => {
    if (this.worksheets.length > 0) {
      const firstWorksheet = this.worksheets[0].worksheet;
      this.setWorksheet(firstWorksheet.id, firstWorksheet, learningPathId);
    }
  }
  loadSelectedWorksheet = () => {
    const worksheet = this.worksheets.find(item => item.worksheet?.id === this.selectedWorksheetId)?.worksheet;
    if (worksheet) {
      this.setWorksheet(worksheet.id, worksheet);
      this.scrollToSelectedWorksheet();
    } else {
      this.loadFirstWorksheet(this.selectedLearningPathId);
    }
  }
  loadSelectedWorksheetFromStorage = (learningPathId) => {
    const storageKey = `learningPath_${learningPathId}_selectedWorksheet`;
    const storedId = localStorage.getItem(storageKey);
    if (storedId) {
      this.selectedWorksheetId = storedId;
    }
  }
  setSelectedWorksheetId = (worksheetId, learningPathId) => {
    this.selectedWorksheetId = worksheetId;
    const storageKey = `learningPath_${learningPathId}_selectedWorksheet`;
    localStorage.setItem(storageKey, worksheetId);
  }
  setWorksheet = (worksheetId, worksheet, learningPathId) => {
    if (learningPathId) {
      this.setSelectedWorksheetId(worksheetId, learningPathId);
    }
    const currentWorksheet = ExerciseUI.worksheetStore.showData;
    if (!currentWorksheet || currentWorksheet.id !== worksheetId) {
      ExerciseUI.set('worksheetId', worksheetId);
      ExerciseUI.worksheetStore.setShowData(worksheet);
    }
    this.enrichWorksheet(worksheetId);
  }

  scrollToSelectedWorksheet() {
    setTimeout(() => {
      const element = document.getElementById(this.selectedWorksheetId);
      if (!element) return;
      const container = document.getElementById('scrollTarget');
      if (!container) return;
      container.scrollTop = element.offsetTop - container.offsetTop;
    }, 100);
  }

  @computed get worksheets() {
    return LearningPathStore.showData?.learningPathWorksheets || [];
  }

  @computed get selectedLearningPathId() {
    return AppUI.cookieStore.selectedLearningPathId;
  }
  handleLearningPathChange = (learningPathId) => {
    this.showWelcomeScreen = false;
    LearningPathStore.clearShowData();
    this.selectedWorksheetId = null;
    AppUI.cookieStore.set('selectedLearningPathId', learningPathId);
    this.loadSelectedWorksheetFromStorage(learningPathId);
    this.loadLearningPath(learningPathId);
    this.updateUserGroupLearningPath(learningPathId);
  }
  updateUserGroupLearningPath = (learningPathId) => {
    if (AppUI.user.currentUserGroup) {
      AppUI.user.currentUserGroup.learningPathId = learningPathId;
      AppUI.userGroupStore.update({
        ids: { userGroupId: AppUI.user.currentUserGroup.id },
        data: { learningPathId },
      });
    }
  }
  enrichWorksheet = (worksheetId) => {
    const currentWorksheet = ExerciseUI.worksheetStore.showData;
    if (currentWorksheet && currentWorksheet.id === worksheetId) {
      const hasExercises = currentWorksheet.exercises && currentWorksheet.exercises.length > 0;
      const hasWorksheetTags = currentWorksheet.worksheetTags && currentWorksheet.worksheetTags.length > 0;
      if (hasExercises || hasWorksheetTags) {
        return;
      }
    }
    LearningPathWorksheetStore.enrichWorksheet({
      ids: { worksheetId },
    }, enrichedData => {
      const currentWorksheet = ExerciseUI.worksheetStore.showData;
      if (currentWorksheet && currentWorksheet.id !== worksheetId) {
        return;
      }
      if (enrichedData.exercises) {
        const exerciseInstances = enrichedData.exercises.map(exerciseData => new Exercise(exerciseData));
        currentWorksheet.exercises = exerciseInstances;
      }
      if (enrichedData.worksheetTags) {
        const worksheetTagInstances = enrichedData.worksheetTags.map(worksheetTagData => new WorksheetTag(worksheetTagData));
        currentWorksheet.worksheetTags = worksheetTagInstances;
      }
      if (enrichedData.podcastEpisode) {
        currentWorksheet.podcastEpisode = new PodcastEpisode(enrichedData.podcastEpisode);
      }
      if (enrichedData.accessStatus) {
        currentWorksheet.accessStatus = enrichedData.accessStatus;
      }
      currentWorksheet.hasPublishedVocab = !!enrichedData.hasPublishedVocab;
    });
  }
  handleWorksheetClick = (worksheetId) => {
    const worksheet = this.worksheets.find(item => item.worksheet?.id === worksheetId)?.worksheet;
    if (worksheet) {
      this.setWorksheet(worksheetId, worksheet, this.selectedLearningPathId);
    }
  }

  trackEventOnce = (eventType, params) => {
    if (this.trackedEvents.has(eventType)) {
      return;
    }

    this.trackedEvents.add(eventType);
    AppUI.createEvent(eventType, params);
  }
  trackViewLearn = (learningPath = null) => {
    this.trackEventOnce('memrev - view learn', eventParamsHelper({
      learning_path_name: learningPath?.name,
      learning_path_id: learningPath?.id,
      level_abbreviation: learningPath?.levelAbbreviation,
    }));
  }

  render() {
    if (!LearningPathStore.hasIndexData) {
      return (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      );
    }

    if (!this.selectedLearningPathId || this.showWelcomeScreen) {
      return (
        <Wrapper>
          <WelcomeScreenWrapper>
            <WelcomeScreen
              learningPaths={LearningPathStore.indexData || []}
              signedIn={!!AppUI.userIsSignedInAndNotGuest}
              onLearningPathClick={this.handleLearningPathChange}
              viewType={AppUI.site.learningPathsWelcomeViewType}
            />
          </WelcomeScreenWrapper>
        </Wrapper>
      );
    }

    if (!LearningPathStore.hasShowData) {
      return (
        <Wrapper>
          <LearningPathDropdown
            value={this.selectedLearningPathId}
            onChange={this.handleLearningPathChange}
          />
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <LearningPathDropdown
          value={this.selectedLearningPathId}
          onChange={this.handleLearningPathChange}
        />
        <PreviewAndPathGrid>
          <DesktopOnly>
            <WorksheetPreviewWrapper id='worksheetPreviewWrapper'>
              <WorksheetPreview />
            </WorksheetPreviewWrapper>
          </DesktopOnly>
          <ScrollTargetWrapper>
            <ScrollTarget secondaryTargetId='worksheetPreviewWrapper'>
              {AppUI.layout.isMobile ? (
                <MobilePath
                  worksheets={this.worksheets}
                  onWorksheetClick={this.handleWorksheetClick}
                />
              ) : (
                <DesktopPath
                  worksheets={this.worksheets}
                  onWorksheetClick={this.handleWorksheetClick}
                />
              )}
            </ScrollTarget>
          </ScrollTargetWrapper>
        </PreviewAndPathGrid>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(LearningPath);